// src/utils/modifierValidator.ts
import {
  ModifierGroup,
  GroupSelections,
  AllModifierSelections,
  ValidationResult,
  AllValidationResults,
} from '../types/modifier';

/**
 * Count total selections in a group
 */
function countSelections(groupSelections: GroupSelections): number {
  if (!groupSelections) return 0;
  
  return Object.values(groupSelections).reduce(
    (total, selection) => total + (selection.quantity || 0),
    0
  );
}

/**
 * Validate a single modifier group
 */
export function validateModifierGroup(
  group: ModifierGroup,
  selections: AllModifierSelections
): ValidationResult {
  const groupSelections = selections[group.id] || {};
  const selectionCount = countSelections(groupSelections);

  // Rule 1: Check if required group has selections
  if (group.isRequired && selectionCount === 0) {
    return {
      isValid: false,
      error: `Please select from "${group.name}"`,
    };
  }

  // Rule 2: Check minimum required selections
  if (group.minRequired > 0 && selectionCount < group.minRequired) {
    return {
      isValid: false,
      error: `Please select at least ${group.minRequired} option(s) from "${group.name}"`,
    };
  }

  // Rule 3: Check maximum allowed selections
  if (group.maxAllowed > 0 && selectionCount > group.maxAllowed) {
    return {
      isValid: false,
      error: `You can select maximum ${group.maxAllowed} option(s) from "${group.name}"`,
    };
  }

  // Rule 4: Check allowMultiples - no duplicate selections
  if (!group.allowMultiples) {
    const hasMultiples = Object.values(groupSelections).some(
      selection => selection.quantity > 1
    );
    
    if (hasMultiples) {
      return {
        isValid: false,
        error: `You can only select each option once in "${group.name}"`,
      };
    }
  }

  // Rule 5: Validate each selected option
  for (const [optionId, selection] of Object.entries(groupSelections)) {
    const option = group.options.find(opt => opt.id === optionId);
    
    if (!option) {
      return {
        isValid: false,
        error: `Invalid option selected in "${group.name}"`,
      };
    }

    // Check if option is enabled
    if (!option.isEnabled) {
      return {
        isValid: false,
        error: `"${option.name}" is currently unavailable`,
      };
    }

    // Rule 6: Validate nested modifiers recursively
    if (option.modifiers && option.modifiers.length > 0) {
      const nestedSelections = selection.nestedSelections || {};
      
      for (const nestedGroup of option.modifiers) {
        // Build proper AllModifierSelections structure for nested validation
        const nestedAllSelections: AllModifierSelections = {};
        nestedAllSelections[nestedGroup.id] = nestedSelections[nestedGroup.id] || {};
        
        const nestedResult = validateModifierGroup(nestedGroup, nestedAllSelections);
        
        if (!nestedResult.isValid) {
          return {
            isValid: false,
            error: `${option.name}: ${nestedResult.error}`,
          };
        }
      }
    }
  }

  return {
    isValid: true,
    error: null,
  };
}

/**
 * Validate all modifier groups
 */
export function validateAllModifiers(
  modifiers: ModifierGroup[],
  selections: AllModifierSelections
): AllValidationResults {
  const errors: Record<string, string> = {};
  let isValid = true;

  for (const group of modifiers) {
    const result = validateModifierGroup(group, selections);
    
    if (!result.isValid) {
      isValid = false;
      errors[group.id] = result.error || 'Invalid selection';
    }
  }

  return {
    isValid,
    errors,
  };
}

